import * as React from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { checkedCircleIconSmall } from "../../../common/checkedCircleIcon";
import { VideoFlowStatusLine } from "./VideoFlowStatusLine";
import { triStateIcon } from "../../../common/triStateIcon";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import { createApi } from "../../../utils/createApi";
import { AxiosResponse } from "axios";
import { timeSinceConvertor } from "./SiteAboutPageUtils/timeSinceConvertor";
import { pointsCreator } from "./SiteAboutPageUtils/pointsCreator";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import useMediaQuery from "@mui/material/useMediaQuery";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  ResposneObject,
  IgnoreZone,
  SiteCamerasResult,
} from "./SiteAboutPageUtils/siteAboutInterfaces";
import { createCameraSettingsIcon } from "./SiteAboutPageUtils/createCameraSettingsIcon";
import { createCameraViewIcon } from "./SiteAboutPageUtils/createCameraViewIcon";
import { resizeImage } from "./SiteAboutPageUtils/resizeImage";
import { Tooltip } from "@mui/material";
import { integrationsMatcher } from "../../../utils/integrationsMatcher";
import { addIgnoreLabel } from "./SiteAboutPageUtils/addIgnoreLabel";
import { isChmOnlyIgnoreZones } from "./SiteAboutPageUtils/isChmOnlyIgnoreZones";
import { useSiteAboutStore } from "./siteAboutStore/siteAboutStore";
import { IgnoreZonesTable } from "./IgnoreZonesTable";
import { MetadataTagZonesTable } from "./MetadataTagZonesTable";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { MetadataTagZone } from "../../EditIMetadataTagZones/EditMetadataTagZonesUtils/editIMetadataTagZonesInterfaces";
import { safeSetItem } from "../../../utils/localStorageUtils";
import { LineCrossingsOverlay } from "./LineCrossingsOverlay";
import { useFlexIgnoreZonesCameraRow } from "./useFlexIgnoreZonesCameraRow";
import { lineCrossingsPresetSearch } from "../../EditLineCrossings/EditLineCrossingsUtils/lineCrossingsListPath";
import { IgnoreZonePreviewHoverLayer } from "../../../common/ignoreZoneHover/IgnoreZonePreviewHoverLayer";
import { formatFrameRate } from "../../EditCamera/EditCameraUtils/formatFrameRate";
// import { SchedulesTabs } from "./SchedulesTabs";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

export const CameraInfoRow = ({
  obj,
  expanded,
  setBackdropLoadingScreenOn,
  siteInfo,
  permissions,
  srcImages,
}: {
  obj: SiteCamerasResult;
  expanded: boolean;
  setBackdropLoadingScreenOn: Dispatcher<boolean>;
  siteInfo: any;
  permissions: any;
  srcImages: any;
}) => {
  const {
    getLineCrossingsData,
    lineCrossingsData,
    presetLineCrossingsData,
    getPresetLineCrossingsData,
    lineCrossingsMetricLabels,
    chmOnlyIgnoreZonesByCamera,
    cameraStatusById,
    ignoreZonesByCamera,
    metadataTagZonesByCamera,
    rowPrefetchStateByCamera,
    setCameraStatusForCamera,
    setIgnoreZonesForCamera,
    setMetadataTagZonesForCamera,
    setRowPrefetchStateForCamera,
    setChmOnlyIgnoreZonesForCamera,
    // metadataTagZones
  } = useSiteAboutStore();

  const autopatrolSite = siteInfo.integration_type_name === "autopatrol";
  const hideCameraIgnoreZonesPanel = Boolean(chmOnlyIgnoreZonesByCamera[obj.id]);

  const [metadataTagZones, setMetadataTagZones] = React.useState<
    MetadataTagZone[]
  >([]);

  const sentinelVerifierSite =
    integrationsMatcher("Name", siteInfo.integration_type) ===
    "Sentinel Verifier"
      ? true
      : false;

  const aiLinkSite =
    integrationsMatcher("Name", siteInfo.integration_type) === "AI Link"
      ? true
      : false;

  const [allIgnoreZones, setAllIgnoreZones] = React.useState<IgnoreZone[]>([]);

  const [visibleIgnoreZones, setVisibleIgnoreZones] = React.useState<
    IgnoreZone[]
  >([]);

  const editCameraText = (
    <>
      {permissions.edit_cameras ? (
        <>
          <EditIcon fontSize="small" />
          &nbsp;Edit Camera
        </>
      ) : (
        <>Full Camera Info</>
      )}
    </>
  );

  const theme = createTheme({
    palette: {
      primary: {
        main: "#FF8400",
      },
    },
  });
  const shrinkLineCrossingsTabs = useMediaQuery("(max-width:1575px)");
  const lineCrossingsTabsWidth = shrinkLineCrossingsTabs ? "305px" : "375px";

  const navigate = useNavigate();

  const [clickedTab, setClickedTab] = React.useState("one");

  const [responseObject, setResponseObject] = React.useState<ResposneObject>({
    // null until /camera/{id}/status/ answers, so an unloaded or failed row
    // shows "No data yet" and not a green tick it never earned (ENG-1782)
    connected: null,
    frame_since_launch: null,
    good_frame: null,
    blur_metric: 0,
    native_frame_rate: 0,
    frame_dimensions: "",
    last_motion: 0,
    preview_url: "",
    health_status: "",
    is_blurry: null,
    aspect_ratio: "",
    comments: "",
    camera_view: "",
    aspect_ratio_status: "unknown",
    frame_rate_status: "unknown",
    resolution_status: "unknown",
    camera_health_comments: "",
    camera_name: "",
    camera_start_time: 0,
    camera_view_comments: "",
    camera_view_status: "unknown",
    codec_status: "unknown",
    display_frame_since_launch: true,
    dynamic_slicing: false,
    id: 0,
    overall_status: "unknown",
  });

  React.useEffect(() => {
    if (!obj.id) {
      return;
    }
    const status = cameraStatusById[obj.id];
    if (status) {
      setResponseObject(status);
      localStorage.setItem("preview_url", JSON.stringify(status.preview_url || ""));
    }

    const ignoreZones = ignoreZonesByCamera[obj.id];
    if (ignoreZones) {
      setAllIgnoreZones(ignoreZones);
      setVisibleIgnoreZones(ignoreZones);
      localStorage.setItem("ignoreZones", JSON.stringify(ignoreZones));
    }

    const cameraTagZones = metadataTagZonesByCamera[obj.id];
    if (cameraTagZones) {
      setMetadataTagZones(cameraTagZones);
    }
  }, [obj.id, cameraStatusById, ignoreZonesByCamera, metadataTagZonesByCamera]);

  const [dimensions, setDimensions] = React.useState<{
    width: number;
    height: number;
  } | null>(null);
  const [previewLoadFailed, setPreviewLoadFailed] = React.useState(false);
  const [previewRefreshRequested, setPreviewRefreshRequested] = React.useState(false);
  const retryAttemptedForCameraRef = React.useRef<{ [cameraId: number]: boolean }>(
    {}
  );

  const handleImageLoad = (
    event: React.SyntheticEvent<HTMLImageElement, Event>
  ) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;

    setDimensions(resizeImage(naturalWidth, naturalHeight));
  };
  const resolvedPreviewUrl = (
    responseObject.preview_url ||
    obj.preview_url ||
    ""
  ).trim();

  React.useEffect(() => {
    setPreviewLoadFailed(false);
    setPreviewRefreshRequested(false);
  }, [resolvedPreviewUrl, expanded, obj.id]);

  React.useEffect(() => {
    if (!obj.id || expanded) {
      return;
    }
    retryAttemptedForCameraRef.current[obj.id] = false;
  }, [expanded, obj.id]);

  React.useEffect(() => {
    if (!expanded || !obj.id) {
      return;
    }
    const prefetchState = rowPrefetchStateByCamera[obj.id];
    if (!prefetchState) {
      return;
    }

    const needsRetry =
      prefetchState.statusFailed ||
      prefetchState.ignoreZonesFailed ||
      prefetchState.tagZonesFailed ||
      prefetchState.lineCrossingsFailed;

    if (!needsRetry || retryAttemptedForCameraRef.current[obj.id]) {
      return;
    }
    retryAttemptedForCameraRef.current[obj.id] = true;

    const retryMissingData = async () => {
      const AxiosUI = createApi("");
      const nextState = { ...prefetchState };

      if (prefetchState.statusFailed) {
        try {
          const statusResponse = await AxiosUI.get(`/camera/${obj.id}/status/`);
          setCameraStatusForCamera(obj.id, statusResponse.data);
          setResponseObject(statusResponse.data);
          nextState.statusLoaded = true;
          nextState.statusFailed = false;
        } catch (_error) {
          // Keep failed status for this camera; user can retry by collapsing/re-expanding.
        }
      }

      if (prefetchState.ignoreZonesFailed) {
        try {
          const ignoreZonesResponse = await AxiosUI.get(
            `/camera/${obj.id}/ignore_zones/`
          );
          const zones = addIgnoreLabel(ignoreZonesResponse.data || []);
          setIgnoreZonesForCamera(obj.id, zones);
          setChmOnlyIgnoreZonesForCamera(
            obj.id,
            isChmOnlyIgnoreZones(ignoreZonesResponse.data || [])
          );
          setAllIgnoreZones(zones);
          setVisibleIgnoreZones(zones);
          nextState.ignoreZonesLoaded = true;
          nextState.ignoreZonesFailed = false;
        } catch (_error) {
          // Keep failed status for this camera; user can retry by collapsing/re-expanding.
        }
      }

      if (prefetchState.tagZonesFailed) {
        try {
          const tagZonesResponse = await AxiosUI.get(`tagzone/?camera=${obj.id}`);
          const zones = tagZonesResponse.data?.results || [];
          setMetadataTagZonesForCamera(obj.id, zones);
          setMetadataTagZones(zones);
          nextState.tagZonesLoaded = true;
          nextState.tagZonesFailed = false;
        } catch (_error) {
          // Keep failed status for this camera; user can retry by collapsing/re-expanding.
        }
      }

      if (prefetchState.lineCrossingsFailed && obj.has_line_crossing) {
        await getLineCrossingsData(navigate, obj.id);
        const currentLineCrossings = useSiteAboutStore.getState().lineCrossingsData;
        if (Object.prototype.hasOwnProperty.call(currentLineCrossings, obj.id)) {
          nextState.lineCrossingsLoaded = true;
          nextState.lineCrossingsFailed = false;
        }
      }

      setRowPrefetchStateForCamera(obj.id, nextState);
    };

    void retryMissingData();
  }, [
    expanded,
    obj.id,
    obj.has_line_crossing,
    rowPrefetchStateByCamera,
    getLineCrossingsData,
    navigate,
    setCameraStatusForCamera,
    setIgnoreZonesForCamera,
    setMetadataTagZonesForCamera,
    setRowPrefetchStateForCamera,
    setChmOnlyIgnoreZonesForCamera,
  ]);

  const refreshPreviewFromApi = React.useCallback(() => {
    if (!obj.id || previewRefreshRequested) {
      return;
    }
    setPreviewRefreshRequested(true);
    const AxiosUI = createApi("");
    AxiosUI.get(`/camera/${obj.id}/status/`)
      .then((response: AxiosResponse) => {
        const nextPreview = (response.data?.preview_url || "").trim();
        setResponseObject((previousState) => ({
          ...previousState,
          ...response.data,
          preview_url: nextPreview || previousState.preview_url,
        }));
        if (nextPreview) {
          setPreviewLoadFailed(false);
        }
      })
      .catch(() => {});
  }, [obj.id, previewRefreshRequested]);

  const { flags } = useFeatureFlag();
  const viewEditMetadataTagZonesPage = flags.viewEditMetadataTagZonesPage;
  const showLineCrossings = flags.showLineCrossings;

  const {
    flexIzActive,
    zonesForPreview,
    zonesForColorSidebar,
    ignoreZonesTitleText,
    ignoreZonesHeadingClassName,
    flexPresetSelectControl,
    flexPresetDialog,
    selectedFlexPresetId,
  } = useFlexIgnoreZonesCameraRow({
    siteInfo,
    obj,
    expanded,
    visibleIgnoreZones,
    allIgnoreZones,
  });

  // Line crossings follow the flex preset picker (ENG-1610): a named preset shows
  // that preset's lines (`?camera=&preset=`), Default shows the camera-wide lines.
  const presetLineCrossingsForSelection =
    selectedFlexPresetId != null
      ? presetLineCrossingsData[obj.id]?.[selectedFlexPresetId]
      : undefined;
  const displayedLineCrossings = React.useMemo(
    () =>
      selectedFlexPresetId != null
        ? presetLineCrossingsForSelection ?? []
        : lineCrossingsData[obj.id] ?? [],
    [selectedFlexPresetId, presetLineCrossingsForSelection, lineCrossingsData, obj.id]
  );

  React.useEffect(() => {
    if (
      !expanded ||
      !obj.has_line_crossing ||
      selectedFlexPresetId == null ||
      presetLineCrossingsForSelection
    ) {
      return;
    }
    void getPresetLineCrossingsData(navigate, obj.id, selectedFlexPresetId);
  }, [
    expanded,
    obj.has_line_crossing,
    obj.id,
    selectedFlexPresetId,
    presetLineCrossingsForSelection,
    getPresetLineCrossingsData,
    navigate,
  ]);

  const editLineCrossingsPath = `/sites/${siteInfo.id}/editcamera/${
    obj.id
  }/editlinecrossings${lineCrossingsPresetSearch(selectedFlexPresetId)}`;

  const ignoreZoneHoverEnabled =
    !hideCameraIgnoreZonesPanel &&
    !previewLoadFailed &&
    !!resolvedPreviewUrl &&
    zonesForPreview.length > 0 &&
    ((!autopatrolSite && clickedTab !== "four") ||
      (autopatrolSite && clickedTab === "one"));

  const ignoreZoneHoverEnabledAilink =
    !hideCameraIgnoreZonesPanel &&
    !previewLoadFailed &&
    !!resolvedPreviewUrl &&
    zonesForPreview.length > 0;

  const cameraTimingSummaryRow = !autopatrolSite ? (
    <div className="mt-[8px] flex flex-col gap-y-2 pt-[8px]">
      <div
        className="h-px w-[200px] max-w-full shrink-0 bg-gray-200"
        aria-hidden
      />
      <div className="w-full min-w-0">
        <div className="font-mulish font-bold text-[11px] leading-tight text-black">
          Last Motion Time
        </div>
        <div className="font-mulish text-[11px] leading-snug text-black">
          {responseObject.last_motion
            ? timeSinceConvertor(Number(responseObject.last_motion))
            : "--"}
        </div>
      </div>
      <div className="w-full min-w-0">
        <div className="font-mulish font-bold text-[11px] leading-tight text-black">
          Camera Start Time
        </div>
        <div className="font-mulish text-[11px] leading-snug text-black">
          {responseObject.camera_start_time
            ? timeSinceConvertor(Number(responseObject.camera_start_time))
            : "--"}
        </div>
      </div>
      {srcImages ? (
        <div className="w-full min-w-0">
          <div className="font-mulish font-bold text-[11px] leading-tight text-black">
            Camera View
          </div>
          <div className="font-mulish text-[11px] leading-snug text-black">
            {createCameraViewIcon(responseObject, srcImages)}
          </div>
        </div>
      ) : null}
      {!hideCameraIgnoreZonesPanel && (
        <div
          className="h-px w-[200px] max-w-full shrink-0 bg-gray-200"
          aria-hidden
        />
      )}
    </div>
  ) : null;

  const cameraSettingsBlock = !autopatrolSite ? (
    <div>
      <div className="font-mulish text-[16px] text-actuate-blue pt-[16px]">
        Camera Settings
      </div>
      <div className="font-mulish text-[12px] text-black pt-[10px] flex">
        <div>
          {createCameraSettingsIcon(
            responseObject.aspect_ratio_status,
            "Aspect Ratio",
            responseObject.comments,
            responseObject.camera_view,
            responseObject
          )}
          &nbsp;&nbsp;Aspect Ratio
        </div>
        <div className="font-mulish font-bold text-[12px] text-black pl-[28px]">
          {responseObject.aspect_ratio ? responseObject.aspect_ratio : "N/A"}
        </div>
      </div>
      <div className="font-mulish text-[12px] text-black pt-[10px] flex">
        <div>
          {createCameraSettingsIcon(
            responseObject.resolution_status,
            "Resolution",
            responseObject.comments,
            responseObject.camera_view,
            responseObject
          )}
          &nbsp;&nbsp;Resolution
        </div>
        <div className="font-mulish font-bold text-[12px] text-black pl-[36px]">
          {responseObject.frame_dimensions
            ? responseObject.frame_dimensions
            : "N/A"}
        </div>
      </div>
      {!sentinelVerifierSite && !aiLinkSite && (
        <div>
          <div className="font-mulish text-[12px] text-black pt-[10px] flex">
            <div>
              {createCameraSettingsIcon(
                responseObject.frame_rate_status,
                "Frame Rate",
                responseObject.comments,
                responseObject.camera_view,
                responseObject
              )}
              &nbsp;&nbsp;Frame Rate
            </div>
            <div className="font-mulish font-bold text-[12px] text-black pl-[32px]">
              {formatFrameRate(responseObject.native_frame_rate)}
            </div>
          </div>
        </div>
      )}
    </div>
  ) : null;

  React.useEffect(() => {
    if (
      hideCameraIgnoreZonesPanel &&
      obj.has_line_crossing &&
      showLineCrossings
    ) {
      setClickedTab("four");
    }
  }, [hideCameraIgnoreZonesPanel, obj.has_line_crossing, showLineCrossings]);

  return obj.id ? (
    <TableRow>
      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 0 }}>
            <div className="relative h-[500px]">
              {!window.location.href.includes("editcamera") && (
                <div className="absolute top-[10px] right-[0px] w-[160px]">
                  <OrangeTextButton
                    onClick={() => {
                      setBackdropLoadingScreenOn(true);
                      localStorage.setItem(
                        "currentCameraId",
                        JSON.stringify(obj.id)
                      );
                      localStorage.setItem("currentCamera", obj.camera_name);
                      // Store only essential camera info to avoid localStorage quota issues
                      safeSetItem("currentCameraInfo", {
                        id: obj.id,
                        camera_name: obj.camera_name,
                      });
                      localStorage.setItem(
                        "preview_url",
                        JSON.stringify(responseObject.preview_url)
                      );
                      localStorage.setItem(
                        "ignoreZones",
                        JSON.stringify(allIgnoreZones)
                      );
                      navigate(`/sites/${siteInfo.id}/editcamera/${obj.id}`);
                    }}
                    text={editCameraText}
                    disabled={false}
                  />
                </div>
              )}
              {siteInfo.integration_type_name !== "ailink" && (
                <div className="absolute top-[20px] left-[575px] w-[380px]">
                  <div className="font-mulish text-[16px] text-actuate-blue">
                    General Information
                  </div>
                  {siteInfo.integration_type_name !== "SMTP_per_camera" &&
                    !autopatrolSite && (
                      <VideoFlowStatusLine
                        siteId={siteInfo.id}
                        cameraId={obj.id}
                        connected={responseObject.connected}
                      />
                    )}
                  <div className="w-full font-mulish text-[12px] text-actuate-blue pt-[10px]">
                    {triStateIcon(
                      responseObject.is_blurry === null
                        ? null
                        : !responseObject.is_blurry
                    )}
                    &nbsp;&nbsp;Blur Metric
                  </div>
                  <div className="w-full font-mulish text-[12px] text-actuate-blue pt-[10px]">
                    {triStateIcon(responseObject.good_frame)}
                    &nbsp;&nbsp;Stream Stability
                  </div>
                  {!autopatrolSite && responseObject.display_frame_since_launch && (
                    <div className="w-full font-mulish text-[12px] text-actuate-blue pt-[10px]">
                      {triStateIcon(responseObject.frame_since_launch)}
                      &nbsp;&nbsp;Frame Since Launch
                    </div>
                  )}
                  {responseObject.dynamic_slicing && (
                    <div className="w-full font-mulish text-[12px] text-actuate-blue pt-[10px]">
                      {checkedCircleIconSmall}
                      &nbsp;&nbsp;Dynamic Slicing
                    </div>
                  )}
                  {cameraTimingSummaryRow}
                  {!hideCameraIgnoreZonesPanel && cameraSettingsBlock}
                </div>
              )}
              {siteInfo.integration_type_name === "ailink" &&
                cameraTimingSummaryRow && (
                  <div className="absolute top-[20px] left-[595px] w-[380px]">
                    {cameraTimingSummaryRow}
                  </div>
                )}
              {!autopatrolSite && (
                <div className="absolute top-[4px] left-[820px] min-h-[300px] w-[375px]">
                  {hideCameraIgnoreZonesPanel && cameraSettingsBlock}
                </div>
              )}
              {!autopatrolSite && (
                <div className="absolute top-[20px] left-[820px] min-h-[300px] w-[375px]">
                  {hideCameraIgnoreZonesPanel &&
                    obj.has_line_crossing &&
                    showLineCrossings && (
                      <div className="w-[375px] pt-[12px]">
                        <div className="font-mulish text-[18px] text-actuate-blue">
                          Line Crossings
                        </div>
                        <div className="pt-[8px]">
                          <OrangeTextButton
                            onClick={() => {
                              setBackdropLoadingScreenOn(true);
                              navigate(editLineCrossingsPath);
                            }}
                            text={
                              <>
                                <EditIcon fontSize="small" />
                                &nbsp;Edit Line Crossings
                              </>
                            }
                            disabled={false}
                          />
                        </div>
                        <div className="relative mt-[8px] w-[375px] max-h-[220px] overflow-auto">
                          {displayedLineCrossings.length > 0 ? (
                            displayedLineCrossings.map((line, index) => {
                              const displayLabels: string[] = [];
                              const hasIntruder = line.metric_labels.some(
                                (id) =>
                                  lineCrossingsMetricLabels.find(
                                    (m) => m.id === id
                                  )?.name === "intruder_line_crossing"
                              );
                              const hasVehicle = line.metric_labels.some(
                                (id) =>
                                  lineCrossingsMetricLabels.find(
                                    (m) => m.id === id
                                  )?.name === "vehicle_line_crossing"
                              );
                              const hasBike = line.metric_labels.some(
                                (id) =>
                                  lineCrossingsMetricLabels.find(
                                    (m) => m.id === id
                                  )?.name === "bike_line_crossing"
                              );

                              if (hasIntruder) displayLabels.push("Intruder");
                              if (hasVehicle || hasBike)
                                displayLabels.push("Vehicle/Bike");

                              const metricsText =
                                displayLabels.length > 0
                                  ? displayLabels.join(", ")
                                  : "No metrics";

                              return (
                                <div key={`line-lc-${line.id || index}`}>
                                  <div
                                    style={{
                                      height: 20,
                                      width: 6,
                                      background: `${line.color}`,
                                    }}
                                    className="absolute"
                                  />
                                  <p className="font-mulish text-[14px] indent-5">
                                    {metricsText}
                                  </p>
                                  &nbsp;
                                </div>
                              );
                            })
                          ) : (
                            <p className="font-mulish text-[14px] text-gray-500">
                              No line crossings configured
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  {!hideCameraIgnoreZonesPanel &&
                    (!obj.has_line_crossing || !showLineCrossings) && (
                      <div className="flex w-[375px] flex-col">
                        <div className="flex items-start gap-2">
                          <p className={ignoreZonesHeadingClassName}>
                            {ignoreZonesTitleText}
                          </p>
                          <div className="shrink-0">
                            <OrangeTextButton
                              onClick={() => {
                                setBackdropLoadingScreenOn(true);
                                localStorage.setItem(
                                  "currentCameraId",
                                  JSON.stringify(obj.id)
                                );
                                localStorage.setItem(
                                  "currentCamera",
                                  obj.camera_name
                                );
                                safeSetItem("currentCameraInfo", {
                                  id: obj.id,
                                  camera_name: obj.camera_name,
                                });
                                localStorage.setItem(
                                  "preview_url",
                                  JSON.stringify(responseObject.preview_url)
                                );
                                localStorage.setItem(
                                  "ignoreZones",
                                  JSON.stringify(allIgnoreZones)
                                );
                                navigate(
                                  `/sites/${siteInfo.id}/editcamera/${obj.id}/editignorezones`
                                );
                              }}
                              text={
                                <Tooltip
                                  title={"Edit Ignore Zones"}
                                  placement="top"
                                  sx={{
                                    fontSize: "20px",
                                    fontFamily: "mulish",
                                  }}
                                >
                                  <EditIcon />
                                </Tooltip>
                              }
                              disabled={false}
                            />
                          </div>
                        </div>
                        {flexPresetSelectControl}
                        <div className="mt-[4px] max-h-[220px] min-h-[150px] w-full overflow-auto">
                          <div className="w-full">
                            {zonesForColorSidebar.map((object: IgnoreZone) => (
                              <div key={object.label} className="relative">
                                <div
                                  style={{
                                    height: 20,
                                    width: 6,
                                    background: `${object.line_color}`,
                                  }}
                                  className="absolute"
                                />
                                <p className="font-mulish text-[14px] indent-5">
                                  {object.label_display_name}
                                </p>
                                &nbsp;
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  {!hideCameraIgnoreZonesPanel &&
                    obj.has_line_crossing &&
                    showLineCrossings && (
                      <div className="relative w-[375px]">
                        <div className="font-mulish text-[18px] text-actuate-blue flex">
                          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                            <ThemeProvider theme={theme}>
                              <Tabs
                                value={clickedTab}
                                onChange={(event, tabValue) => {
                                  setClickedTab(tabValue);
                                }}
                                aria-label="intestigation_tabs"
                                color="primary"
                                sx={{ width: lineCrossingsTabsWidth }}
                              >
                                <Tab
                                  value="one"
                                  label={ignoreZonesTitleText}
                                  sx={{
                                    fontFamily: "Mulish",
                                    fontWeight: "Bold",
                                    color: "primary",
                                    fontSize: "16px",
                                  }}
                                />
                                <Tab
                                  value="four"
                                  label={`Line Crossings`}
                                  sx={{
                                    fontFamily: "Mulish",
                                    fontWeight: "Bold",
                                    color: "primary",
                                    fontSize: "16px",
                                  }}
                                />
                              </Tabs>
                            </ThemeProvider>
                          </Box>
                        </div>
                        {clickedTab === "one" && (
                          <div className="absolute pt-[5px]">
                            <OrangeTextButton
                              onClick={() => {
                                setBackdropLoadingScreenOn(true);
                                navigate(
                                  `/sites/${siteInfo.id}/editcamera/${obj.id}/editignorezones`
                                );
                              }}
                              text={
                                <>
                                  <EditIcon fontSize="small" />
                                  &nbsp;Edit Ignore Zones
                                </>
                              }
                              disabled={false}
                            />
                          </div>
                        )}
                        {clickedTab === "one" && (
                          <div className="relative left-[0px] top-[50px] w-[375px]">
                            {flexPresetSelectControl}
                            <div className="h-[250px] w-full overflow-auto pt-[10px]">
                              {zonesForColorSidebar.map((object: IgnoreZone) => (
                                <div key={object.label} className="relative">
                                  <div
                                    style={{
                                      height: 20,
                                      width: 6,
                                      background: `${object.line_color}`,
                                    }}
                                    className="absolute"
                                  />
                                  <p className="font-mulish text-[14px] indent-5">
                                    {object.label_display_name}
                                  </p>
                                  &nbsp;
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {clickedTab === "four" && (
                          <div className="absolute pt-[5px]">
                            <OrangeTextButton
                              onClick={() => {
                                setBackdropLoadingScreenOn(true);
                                navigate(editLineCrossingsPath);
                              }}
                              text={
                                <>
                                  <EditIcon fontSize="small" />
                                  &nbsp;Edit Line Crossings
                                </>
                              }
                              disabled={false}
                            />
                          </div>
                        )}
                        {clickedTab === "four" && (
                          <div className="relative left-[0px] top-[50px] w-[375px] h-[130px] overflow-auto">
                            {displayedLineCrossings.length > 0 ? (
                              displayedLineCrossings.map((line, index) => {
                                const displayLabels: string[] = [];
                                const hasIntruder = line.metric_labels.some(
                                  (id) =>
                                    lineCrossingsMetricLabels.find(
                                      (m) => m.id === id
                                    )?.name === "intruder_line_crossing"
                                );
                                const hasVehicle = line.metric_labels.some(
                                  (id) =>
                                    lineCrossingsMetricLabels.find(
                                      (m) => m.id === id
                                    )?.name === "vehicle_line_crossing"
                                );
                                const hasBike = line.metric_labels.some(
                                  (id) =>
                                    lineCrossingsMetricLabels.find(
                                      (m) => m.id === id
                                    )?.name === "bike_line_crossing"
                                );

                                if (hasIntruder) displayLabels.push("Intruder");
                                if (hasVehicle || hasBike)
                                  displayLabels.push("Vehicle/Bike");

                                const metricsText =
                                  displayLabels.length > 0
                                    ? displayLabels.join(", ")
                                    : "No metrics";

                                return (
                                  <div key={`line-${line.id || index}`}>
                                    <div
                                      style={{
                                        height: 20,
                                        width: 6,
                                        background: `${line.color}`,
                                      }}
                                      className="absolute"
                                    />
                                    <p className="font-mulish text-[14px] indent-5">
                                      {metricsText}
                                    </p>
                                    &nbsp;
                                  </div>
                                );
                              })
                            ) : (
                              <p className="font-mulish text-[14px] text-gray-500">
                                No line crossings configured
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                </div>
              )}
              {autopatrolSite &&
                viewEditMetadataTagZonesPage &&
                !hideCameraIgnoreZonesPanel && (
                  <div className="absolute top-[5px] left-[800px] w-[400px] h-[300px]">
                    <div className="font-mulish text-[18px] text-actuate-blue flex">
                      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <ThemeProvider theme={theme}>
                          <Tabs
                            value={clickedTab}
                            onChange={(event, tabValue) => {
                              setClickedTab(tabValue);
                            }}
                            aria-label="intestigation_tabs"
                            color="primary"
                            sx={{ width: "345px" }}
                          >
                            <Tab
                              value="one"
                              label={ignoreZonesTitleText}
                              sx={{
                                fontFamily: "Mulish",
                                fontWeight: "Bold",
                                color: "primary",
                                fontSize: "16px",
                              }}
                            />
                            <Tab
                              value="two"
                              label={`Metadata Tag Zones`}
                              sx={{
                                fontFamily: "Mulish",
                                fontWeight: "Bold",
                                color: "primary",
                                fontSize: "16px",
                              }}
                            />
                          </Tabs>
                        </ThemeProvider>
                      </Box>
                    </div>
                    <div>
                      {clickedTab === "one" && (
                        <>
                          {flexPresetSelectControl}
                          <IgnoreZonesTable
                            visibleIgnoreZones={
                              flexIzActive ? zonesForPreview : visibleIgnoreZones
                            }
                            onEditClick={() => {
                              setBackdropLoadingScreenOn(true);
                              navigate(
                                `/sites/${siteInfo.id}/editcamera/${obj.id}/editignorezones`
                              );
                            }}
                          />
                        </>
                      )}
                      {clickedTab === "two" && (
                        <MetadataTagZonesTable
                          metadataTagZones={metadataTagZones}
                          onEditClick={() => {
                            setBackdropLoadingScreenOn(true);
                            navigate(
                              `/sites/${siteInfo.id}/editcamera/${obj.id}/editmetadatatagzones`
                            );
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              {!autopatrolSite ||
                (!viewEditMetadataTagZonesPage &&
                  !hideCameraIgnoreZonesPanel && (
                  <div className="absolute top-[20px] left-[825px] flex min-h-[300px] w-[320px] flex-col">
                    <div className="flex items-start gap-2">
                      <p className={ignoreZonesHeadingClassName}>
                        {ignoreZonesTitleText}
                      </p>
                      <div className="shrink-0">
                        <OrangeTextButton
                          onClick={() => {
                            setBackdropLoadingScreenOn(true);
                            localStorage.setItem(
                              "currentCameraId",
                              JSON.stringify(obj.id)
                            );
                            localStorage.setItem(
                              "currentCamera",
                              obj.camera_name
                            );
                            // Store only essential camera info to avoid localStorage quota issues
                            safeSetItem("currentCameraInfo", {
                              id: obj.id,
                              camera_name: obj.camera_name,
                            });
                            localStorage.setItem(
                              "preview_url",
                              JSON.stringify(responseObject.preview_url)
                            );
                            localStorage.setItem(
                              "ignoreZones",
                              JSON.stringify(allIgnoreZones)
                            );
                            navigate(
                              `/sites/${siteInfo.id}/editcamera/${obj.id}/editignorezones`
                            );
                          }}
                          text={
                            <Tooltip
                              title={"Edit Ignore Zones"}
                              placement="top"
                              sx={{ fontSize: "20px", fontFamily: "mulish" }}
                            >
                              <EditIcon />
                            </Tooltip>
                          }
                          disabled={false}
                        />
                      </div>
                    </div>
                    {flexPresetSelectControl}
                    <div className="mt-[4px] max-h-[200px] min-h-[150px] w-[305px] overflow-auto">
                      <div className="w-[275px]">
                        {zonesForColorSidebar.map((object: IgnoreZone, zi: number) => (
                          <div
                            key={`${obj.id}-sidebar-${zi}-${object.metric_id}-${object.preset_zone_id ?? object.label}`}
                            className="relative"
                          >
                            <div
                              style={{
                                height: 20,
                                width: 6,
                                background: `${object.line_color}`,
                              }}
                              className="absolute"
                            />
                            <p className="font-mulish text-[14px] indent-5">
                              {object.label_display_name}
                            </p>
                            &nbsp;
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              {siteInfo.integration_type_name !== "ailink" ? (
                <IgnoreZonePreviewHoverLayer
                  className="absolute top-[20px]"
                  zones={zonesForPreview}
                  enabled={ignoreZoneHoverEnabled}
                >
                  {!previewLoadFailed && resolvedPreviewUrl && (
                    <img
                      src={resolvedPreviewUrl}
                      style={{
                        width: dimensions ? `${dimensions.width}px` : "auto",
                        height: dimensions ? `${dimensions.height}px` : "auto",
                      }}
                      onLoad={handleImageLoad}
                      onError={() => {
                        setPreviewLoadFailed(true);
                        refreshPreviewFromApi();
                      }}
                      alt="TestCameraPhoto"
                    />
                  )}
                  {(previewLoadFailed || !resolvedPreviewUrl) && (
                    <p className="font-mulish text-[14px] text-gray-600">
                      Camera preview unavailable.
                    </p>
                  )}
                  {/* Render ignore zones when NOT on Line Crossings tab */}
                  {!autopatrolSite &&
                    !hideCameraIgnoreZonesPanel &&
                    clickedTab !== "four" &&
                    !previewLoadFailed &&
                    !!resolvedPreviewUrl &&
                    zonesForPreview.map((object: IgnoreZone, zi: number) => {
                      return (
                        <div
                          className="absolute top-[0px]"
                          key={`${obj.id}-iz-${zi}-${object.metric_id}-${object.preset_zone_id ?? object.label}`}
                        >
                          {object.coordinates.map(
                            (array: number[], index: number) => (
                              <svg
                                className="absolute top-[0px]"
                                key={index}
                                style={{
                                  width: dimensions
                                    ? `${dimensions.width}px`
                                    : "auto",
                                  height: dimensions
                                    ? `${dimensions.height}px`
                                    : "auto",
                                }}
                              >
                                {dimensions && (
                                  <polygon
                                    points={pointsCreator(
                                      object.coordinates[index],
                                      dimensions.height,
                                      dimensions.width
                                    )}
                                    stroke={object.line_color}
                                    strokeWidth="5"
                                    fill={object.line_color}
                                    fillOpacity="50%"
                                  />
                                )}
                              </svg>
                            )
                          )}
                        </div>
                      );
                    })}
                  {/* Render line crossings when on Line Crossings tab */}
                  {!autopatrolSite &&
                    dimensions &&
                    displayedLineCrossings.length > 0 &&
                    (clickedTab === "four" ||
                      (hideCameraIgnoreZonesPanel &&
                        obj.has_line_crossing &&
                        showLineCrossings)) && (
                      <LineCrossingsOverlay
                        lines={displayedLineCrossings}
                        width={dimensions.width}
                        height={dimensions.height}
                        frameDimensions={responseObject.frame_dimensions}
                      />
                    )}
                  {autopatrolSite &&
                    !hideCameraIgnoreZonesPanel &&
                    clickedTab === "one" &&
                    zonesForPreview.map((object: IgnoreZone, zi: number) => {
                      return (
                        <div
                          className="absolute top-[0px]"
                          key={`${obj.id}-iz-ap-${zi}-${object.metric_id}-${object.preset_zone_id ?? object.label}`}
                        >
                          {object.coordinates.map(
                            (array: number[], index: number) => (
                              <svg
                                className="absolute top-[0px]"
                                key={index}
                                style={{
                                  width: dimensions
                                    ? `${dimensions.width}px`
                                    : "auto",
                                  height: dimensions
                                    ? `${dimensions.height}px`
                                    : "auto",
                                }}
                              >
                                {dimensions && (
                                  <polygon
                                    points={pointsCreator(
                                      object.coordinates[index],
                                      dimensions.height,
                                      dimensions.width
                                    )}
                                    stroke={object.line_color}
                                    strokeWidth="5"
                                    fill={object.line_color}
                                    fillOpacity="50%"
                                  />
                                )}
                              </svg>
                            )
                          )}
                        </div>
                      );
                    })}
                  {autopatrolSite &&
                    clickedTab === "two" &&
                    metadataTagZones.map((object: any) => {
                      return (
                        <div className="absolute top-[0px]" key={object.label}>
                          {object.coordinates.map(
                            (array: number[], index: number) => (
                              <svg
                                className="absolute top-[0px]"
                                key={index}
                                style={{
                                  width: dimensions
                                    ? `${dimensions.width}px`
                                    : "auto",
                                  height: dimensions
                                    ? `${dimensions.height}px`
                                    : "auto",
                                }}
                              >
                                {dimensions && (
                                  <polygon
                                    points={pointsCreator(
                                      object.coordinates[index],
                                      dimensions.height,
                                      dimensions.width
                                    )}
                                    stroke={object.line_color}
                                    strokeWidth="5"
                                    fill={object.line_color}
                                    fillOpacity="50%"
                                  />
                                )}
                              </svg>
                            )
                          )}
                        </div>
                      );
                    })}
                </IgnoreZonePreviewHoverLayer>
              ) : (
                <IgnoreZonePreviewHoverLayer
                  className="absolute top-[20px]"
                  zones={zonesForPreview}
                  enabled={ignoreZoneHoverEnabledAilink}
                >
                  {!previewLoadFailed && resolvedPreviewUrl && (
                    <img
                      src={resolvedPreviewUrl}
                      onLoad={handleImageLoad}
                      onError={() => {
                        setPreviewLoadFailed(true);
                        refreshPreviewFromApi();
                      }}
                      style={{
                        width: dimensions ? `${dimensions.width}px` : "auto",
                        height: dimensions ? `${dimensions.height}px` : "auto",
                      }}
                      alt="TestCameraPhoto"
                    />
                  )}
                  {(previewLoadFailed || !resolvedPreviewUrl) && (
                    <p className="font-mulish text-[14px] text-gray-600">
                      Camera preview unavailable.
                    </p>
                  )}
                  {!hideCameraIgnoreZonesPanel &&
                    !previewLoadFailed &&
                    !!resolvedPreviewUrl &&
                    zonesForPreview.map((object: IgnoreZone, zi: number) => {
                    return (
                      <div
                        className="absolute top-[0px]"
                        key={`${obj.id}-iz-al-${zi}-${object.metric_id}-${object.preset_zone_id ?? object.label}`}
                      >
                        {object.coordinates.map(
                          (array: number[], index: number) => {
                            return (
                              <svg
                                className="absolute top-[0px]"
                                key={`${zi}-${index}-${array[0]}`}
                                style={{
                                  width: dimensions
                                    ? `${dimensions.width}px`
                                    : "auto",
                                  height: dimensions
                                    ? `${dimensions.height}px`
                                    : "auto",
                                }}
                              >
                                {dimensions && (
                                  <polygon
                                    points={pointsCreator(
                                      object.coordinates[index],
                                      dimensions.height,
                                      dimensions.width
                                    )}
                                    stroke={object.line_color}
                                    strokeWidth="5"
                                    fill={object.line_color}
                                    fillOpacity="50%"
                                  />
                                )}
                              </svg>
                            );
                          }
                        )}
                      </div>
                    );
                  })}
                </IgnoreZonePreviewHoverLayer>
              )}
            </div>
            {!hideCameraIgnoreZonesPanel && flexPresetDialog}
          </Box>
        </Collapse>
      </TableCell>
    </TableRow>
  ) : (
    <></>
  );
};
